import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';

export type UserType = 'empresa' | 'candidato' | 'admin';

export interface User {
  id: string;
  email: string;
  userType: UserType;
  nombre?: string;
  // Campos para empresa
  nombreEmpresa?: string;
  sector?: string;
  tamanoEmpresa?: string;
  // Campos para candidato
  experiencia?: string;
  pais?: string;
  presentacion?: string;
  disponibilidad?: 'disponible' | 'en-proceso' | 'no-disponible';
  disponibilidadGeografica?: string[];
  hasLibraryAccess?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  authLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: Partial<User> & { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  adminLogin: (email: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Leer credenciales admin desde variables de entorno
const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL || 'beaaraquee@gmail.com';
const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD || '123456789';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Mapear usuario de Supabase a nuestro tipo User
  const mapSupabaseUser = (supabaseUser: SupabaseUser): User => {
    const metadata = supabaseUser.user_metadata || {};
    return {
      id: supabaseUser.id,
      email: supabaseUser.email!,
      userType: metadata.userType || 'candidato',
      nombre: metadata.nombre,
      nombreEmpresa: metadata.nombreEmpresa,
      sector: metadata.sector,
      tamanoEmpresa: metadata.tamanoEmpresa,
      experiencia: metadata.experiencia,
      pais: metadata.pais,
      presentacion: metadata.presentacion,
      disponibilidad: metadata.disponibilidad,
      disponibilidadGeografica: metadata.disponibilidadGeografica,
      hasLibraryAccess: metadata.hasLibraryAccess || false,
    };
  };

  // Verificar sesión al cargar y escuchar cambios
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // Verificar si hay sesión de admin en localStorage
        const adminSession = localStorage.getItem('adminSession');
        if (adminSession === 'true') {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              if (parsedUser.email === ADMIN_EMAIL && parsedUser.userType === 'admin') {
                if (mounted) {
                  setUser(parsedUser);
                  setIsAuthenticated(true);
                  setAuthLoading(false);
                }
                return; // No continuar: admin ya restaurado
              }
            } catch {
              localStorage.removeItem('user');
              localStorage.removeItem('adminSession');
            }
          }
        }

        // Verificar sesión actual de Supabase
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) {
          if (session?.user) {
            const mappedUser = mapSupabaseUser(session.user);
            setUser(mappedUser);
            setIsAuthenticated(true);
          }
          setAuthLoading(false);
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error);
        if (mounted) {
          setAuthLoading(false);
        }
      }
    };

    initAuth();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;

      // Si hay sesión de admin activa en localStorage, no interferir
      const isAdminActive = localStorage.getItem('adminSession') === 'true';
      if (isAdminActive) return;

      if (session?.user) {
        const mappedUser = mapSupabaseUser(session.user);
        setUser(mappedUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    // Si es el correo de admin, usar flujo de administrador
    if (email === ADMIN_EMAIL) {
      if (password === ADMIN_PASSWORD) {
        const adminUser: User = {
          id: 'admin-001',
          email: ADMIN_EMAIL,
          userType: 'admin',
          nombre: 'Administrador'
        };
        setUser(adminUser);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(adminUser));
        localStorage.setItem('adminSession', 'true');
        return;
      } else {
        throw new Error('Credenciales incorrectas');
      }
    }

    // Flujo normal con Supabase
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Email o contraseña incorrectos');
      }
      throw new Error(error.message);
    }

    if (data.user) {
      const mappedUser = mapSupabaseUser(data.user);
      setUser(mappedUser);
      setIsAuthenticated(true);
    }
  };

  const adminLogin = async (email: string, password: string) => {
    // Login exclusivo para administrador
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      const adminUser: User = {
        id: 'admin-001',
        email: ADMIN_EMAIL,
        userType: 'admin',
        nombre: 'Administrador'
      };
      setUser(adminUser);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(adminUser));
      localStorage.setItem('adminSession', 'true');
    } else {
      throw new Error('Credenciales de administrador incorrectas');
    }
  };

  const register = async (userData: Partial<User> & { email: string; password: string }) => {
    const { email, password, ...metadata } = userData;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          userType: metadata.userType,
          nombre: metadata.nombre,
          nombreEmpresa: metadata.nombreEmpresa,
          sector: metadata.sector,
          tamanoEmpresa: metadata.tamanoEmpresa,
          experiencia: metadata.experiencia,
          pais: metadata.pais,
          disponibilidad: metadata.userType === 'candidato' ? 'disponible' : undefined,
          disponibilidadGeografica: metadata.userType === 'candidato' ? [] : undefined,
          hasLibraryAccess: false,
        },
      },
    });

    if (error) {
      if (error.message.includes('User already registered')) {
        throw new Error('Este email ya está registrado');
      }
      if (error.message.includes('Password should be at least 6 characters')) {
        throw new Error('La contraseña debe tener al menos 6 caracteres');
      }
      throw new Error(error.message);
    }

    // Si Supabase devuelve sesión directamente (modo sin confirmación de email)
    if (data.session && data.user) {
      const mappedUser = mapSupabaseUser(data.user);
      setUser(mappedUser);
      setIsAuthenticated(true);

      // Crear registro en tabla candidates si es candidato
      if (metadata.userType === 'candidato') {
        await supabase.from('candidates').upsert({
          user_id: data.user.id,
          email: data.user.email,
          name: metadata.nombre || '',
          position: metadata.sector || '',
          experience: metadata.experiencia || '',
          location: metadata.pais || '',
          status: 'pendiente',
        }, {
          onConflict: 'user_id'
        });
      }
      return;
    }

    // Si no hay sesión pero sí usuario (pendiente de confirmación), intentar login directo
    if (data.user && !data.session) {
      try {
        const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (!loginError && loginData.user) {
          const mappedUser = mapSupabaseUser(loginData.user);
          setUser(mappedUser);
          setIsAuthenticated(true);

          // Crear registro en tabla candidates si es candidato
          if (metadata.userType === 'candidato') {
            await supabase.from('candidates').upsert({
              user_id: loginData.user.id,
              email: loginData.user.email,
              name: metadata.nombre || '',
              position: metadata.sector || '',
              experience: metadata.experiencia || '',
              location: metadata.pais || '',
              status: 'pendiente',
            }, {
              onConflict: 'user_id'
            });
          }
          return;
        }
      } catch {
        // Si el login automático falla, continuar sin sesión
      }
    }
  };

  const logout = async () => {
    // Si es admin, solo limpiar localStorage
    if (user?.userType === 'admin') {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('user');
      localStorage.removeItem('adminSession');
      return;
    }

    // Logout de Supabase
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error al cerrar sesión:', error);
    }
    
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUserProfile = async (data: Partial<User>) => {
    if (!user) return;

    // Si es admin, solo actualizar localStorage
    if (user.userType === 'admin') {
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return;
    }

    // Actualizar en Supabase
    const { error } = await supabase.auth.updateUser({
      data: {
        ...data,
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    const updatedUser = { ...user, ...data };
    setUser(updatedUser);

    // Si es candidato, sincronizar con tabla candidates
    if (user.userType === 'candidato') {
      await supabase.from('candidates').upsert({
        user_id: user.id,
        email: user.email,
        name: data.nombre || user.nombre || '',
        position: data.sector || user.sector || '',
        experience: data.experiencia || user.experiencia || '',
        location: data.pais || user.pais || '',
        status: data.disponibilidad || user.disponibilidad || 'pendiente',
      }, {
        onConflict: 'user_id'
      });
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://bnagenzia.com',
    });

    if (error) {
      throw new Error(error.message);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      authLoading,
      login, 
      register, 
      logout, 
      updateUserProfile, 
      resetPassword,
      adminLogin 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
}